import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CForm,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CFormText,
  CInputGroup,
  CRow,
  CCol,
  CSpinner,
  CBadge,
  CAvatar,
} from "@coreui/react";

import { apiClient } from "services/Api";
import TextEditor from "components/forms/TextEditor";
import SearchSelect from "components/forms/SearchSelect";
import SheetTableCamp from "views/applications/_components/SheetTableCamp";
import ToastNotification from "components/globals/ToastNotification";
import { triggerAlert } from "components/globals/AlertNotification";
import userAvatar from "assets/images/avatars/user.png";
import CIcon from "@coreui/icons-react";
import { cilSearch } from "@coreui/icons";

const newCompanyOptions = [
  { label: "Sim", value: true },
  { label: "Não", value: false },
];

const companySizeOptions = [
  { label: "Micro", value: "Micro" },
  { label: "Pequena", value: "Pequena" },
  { label: "Média", value: "Média" },
  { label: "Grande", value: "Grande" },
];

const CP_REGEX = /^\d{4}-\d{3}$/;

const CLASSIFICATION_COLORS = {
  "Fora do Mapa de Auxílios regionais": {
    bg: "#f8d7da",
    border: "#dc3545",
    text: "#842029",
  },
  "Território Baixa Densidade": {
    bg: "#fff3cd",
    border: "#fd7e14",
    text: "#664d03",
  },
  "Território Subdesenvolvidos": {
    bg: "#d1e7dd",
    border: "#198754",
    text: "#0a3622",
  },
};
const DEFAULT_COLOR = { bg: "#e2e3e5", border: "#6c757d", text: "#41464b" };

const projectTypeOptions = [
  "SI Inovação",
  "SI Internacionalização",
  "SI Qualificação",
  "SI I&DT",
  "SI I&DT Co-Promoção",
  "Projeto IDI",
  "Outros",
];

const KickoffSession = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(null);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [applications, setApplications] = useState([]);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [postalCode, setPostalCode] = useState("");
  const [classification, setClassification] = useState(null);
  const [classificationLoading, setClassificationLoading] = useState(false);

  const fetchSessionDetails = async () => {
    try {
      const response = await apiClient.get(`/kickoff-sessions/${id}/`);
      setFormData(response.data);
      if (response.data.postal_code) {
        setPostalCode(response.data.postal_code);
      }
      if (response.data.client) {
        fetchApplications(response.data.client);
      }
    } catch (error) {
      triggerAlert("Sessão de arranque não encontrada!", "error");
      navigate("/sessoes-arranque");
    }
  };

  const fetchClients = async () => {
    try {
      const response = await apiClient.get("/clients/");
      setClients(response.data || []);
    } catch (error) {
      setClients([]);
    }
  };

  const fetchApplications = async (clientId) => {
    if (!clientId) {
      setApplications([]);
      return;
    }
    setApplicationsLoading(true);
    try {
      const response = await apiClient.get(
        `/applications/by-client/${clientId}/`,
      );
      setApplications(response.data || []);
    } catch (error) {
      setApplications([]);
    } finally {
      setApplicationsLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
    if (id) {
      fetchSessionDetails();
    } else {
      setFormData({
        company_name: "",
        client: null,
        application: null,
        is_new_company: null,
        company_size: "",
        location: "",
        postal_code: "",
        territorial_classification: "",
        constraints: "",
        contacts: [],
        company_activity_summary: "",
        project_type: "",
        planning_schedule: "",
        investment_list: "",
        innovation_aspects: "",
        product: "",
        process: "",
        organizational: "",
        marketing: "",
        post_project_markets: [],
        prepared_by: "",
      });
    }
  }, [id]);

  const clientOptions = useMemo(
    () => clients.map((client) => ({ label: client.name, value: client.id })),
    [clients],
  );

  const applicationOptions = useMemo(
    () =>
      applications.map((app) => ({
        label: `${app.designation || "Sem Designação"} • ${app.notice}`,
        value: app.id,
      })),
    [applications],
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value === "" ? null : value,
    }));
  };

  const handleClientChange = (clientId, option) => {
    const companyName = option?.label || formData.company_name || "";
    setFormData((prev) => ({
      ...prev,
      client: clientId,
      company_name: companyName,
      application: null,
    }));
    fetchApplications(clientId);
  };

  const handleApplicationChange = (applicationId) => {
    setFormData((prev) => ({
      ...prev,
      application: applicationId,
    }));
  };

  const contactColumns = useMemo(
    () => [
      { title: "Nome", data: "name", type: "text", width: "180px" },
      { title: "Email", data: "email", type: "text", width: "220px" },
      { title: "Telemóvel", data: "phone", type: "text", width: "140px" },
      { title: "Função", data: "role", type: "text", width: "180px" },
      {
        title: "Decisor",
        data: "decision_maker",
        type: "checkbox",
        width: "90px",
        className: "htCenter htMiddle",
      },
      {
        title: "Elemento de contacto",
        data: "contact_person",
        type: "checkbox",
        width: "150px",
        className: "htCenter htMiddle",
      },
    ],
    [],
  );

  const marketColumns = useMemo(
    () => [
      { title: "Mercado", data: "market", type: "text", width: "120px" },
      { title: "IE", data: "ie", type: "text", width: "70px" },
      {
        title: "Nº ações",
        data: "prospect_actions",
        type: "text",
        width: "110px",
      },
      {
        title: "Segmento",
        data: "segment_target",
        type: "text",
        width: "140px",
      },
      {
        title: "Canais",
        data: "distribution_channels",
        type: "text",
        width: "140px",
      },
      {
        title: "Parceiros",
        data: "partners",
        type: "text",
        width: "140px",
      },
      {
        title: "Acordos",
        data: "agreements",
        type: "text",
        width: "140px",
      },
    ],
    [],
  );

  const handlePostalCodeLookup = async () => {
    if (!CP_REGEX.test(postalCode)) return;
    setClassificationLoading(true);
    setClassification(null);
    try {
      const res = await apiClient.get(
        `/postal-code-lookup/?cp=${encodeURIComponent(postalCode)}`,
      );
      setClassification(res.data);
      setFormData((prev) => ({
        ...prev,
        postal_code: postalCode,
        territorial_classification: res.data.classificacao,
      }));
    } catch {
      setClassification({ error: true });
    } finally {
      setClassificationLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (id) {
        await apiClient.put(`/kickoff-sessions/${id}/`, formData);
        triggerAlert("Sessão de arranque atualizada com sucesso!", "success");
      } else {
        await apiClient.post("/kickoff-sessions/", formData);
        triggerAlert("Sessão de arranque criada com sucesso!", "success");
      }
      navigate("/sessoes-arranque");
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || "Erro ao gravar sessão de arranque";
      setToast({
        title: "Sessões de Arranque",
        message: errorMessage,
        type: "error",
      });
      setLoading(false);
    }
  };

  if (!formData) {
    return (
      <div className="text-center">
        <CSpinner color="primary" />
      </div>
    );
  }

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString("pt-PT");
  };

  return (
    <div className="container mt-4">
      <ToastNotification toast={toast} setToast={setToast} />

      <CCard className="shadow-sm">
        <CCardHeader className="d-flex justify-content-between align-items-center">
          <div>
            <h4 className="mb-1">
              {id ? "Editar Sessão de Arranque" : "Nova Sessão de Arranque"}
            </h4>
            {id && (
              <div className="d-flex align-items-center gap-3 mt-2">
                <CAvatar
                  src={formData.created_by_avatar_url || userAvatar}
                  size="md"
                />
                <div className="text-medium-emphasis small">
                  <div className="fw-semibold text-body">
                    {formData.created_by_name || "-"}
                  </div>
                  <div>
                    Criado em {formatDateTime(formData.created_at)} • Atualizado
                    em {formatDateTime(formData.updated_at)}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="d-flex gap-2">
            <CButton
              color="secondary"
              variant="outline"
              onClick={() => navigate("/sessoes-arranque")}
              disabled={loading}
            >
              Cancelar
            </CButton>
            <CButton color="info" className="text-white" onClick={handleSubmit}>
              {loading ? "A guardar..." : "Guardar"}
            </CButton>
          </div>
        </CCardHeader>
        <CCardBody>
          <CForm onSubmit={handleSubmit}>
            <CFormText className="text-muted mb-4">
              Campos obrigatórios assinalados com *
            </CFormText>

            <div className="border rounded-3 p-3 bg-body-tertiary mb-4">
              <div className="text-uppercase text-medium-emphasis small mb-3">
                Informação geral da empresa
              </div>
              <CRow className="g-3">
                <CCol md={6}>
                  <SearchSelect
                    label="Cliente (opcional)"
                    options={clientOptions}
                    value={formData.client}
                    onChange={handleClientChange}
                    placeholder="Selecionar cliente..."
                  />
                  {formData.client && (
                    <div className="mt-2 text-medium-emphasis small">
                      Ligado ao cliente{" "}
                      <CBadge color="info">
                        {formData.company_name || "Sem nome"}
                      </CBadge>
                    </div>
                  )}
                </CCol>
                <CCol md={6}>
                  <CFormInput
                    label="Designação Empresa *"
                    name="company_name"
                    value={formData.company_name || ""}
                    onChange={handleInputChange}
                    disabled={Boolean(formData.client)}
                    required
                  />
                </CCol>
                <CCol md={6}>
                  <CFormSelect
                    label="Nova empresa"
                    name="is_new_company"
                    value={
                      formData.is_new_company === true
                        ? "true"
                        : formData.is_new_company === false
                          ? "false"
                          : ""
                    }
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        is_new_company:
                          e.target.value === ""
                            ? null
                            : e.target.value === "true",
                      }))
                    }
                  >
                    <option value="">Selecione...</option>
                    {newCompanyOptions.map((opt) => (
                      <option key={opt.label} value={String(opt.value)}>
                        {opt.label}
                      </option>
                    ))}
                  </CFormSelect>
                </CCol>
                <CCol md={6}>
                  <CFormSelect
                    label="Dimensão"
                    name="company_size"
                    value={formData.company_size || ""}
                    onChange={handleInputChange}
                  >
                    <option value="">Selecione...</option>
                    {companySizeOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </CFormSelect>
                </CCol>
                <CCol md={6}>
                  <CFormInput
                    label="Localização"
                    name="location"
                    value={formData.location || ""}
                    onChange={handleInputChange}
                  />
                </CCol>
                <CCol md={6}>
                  <CFormLabel>Código Postal</CFormLabel>
                  <CInputGroup>
                    <CFormInput
                      placeholder="XXXX-XXX"
                      value={postalCode}
                      onChange={(e) => {
                        setPostalCode(e.target.value);
                        setClassification(null);
                        setFormData((prev) => ({
                          ...prev,
                          postal_code: e.target.value,
                          territorial_classification: "",
                        }));
                      }}
                      onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                      maxLength={8}
                      style={{ maxWidth: 120 }}
                    />
                    <CButton
                      color="secondary"
                      onClick={handlePostalCodeLookup}
                      disabled={
                        classificationLoading || !CP_REGEX.test(postalCode)
                      }
                    >
                      {classificationLoading ? (
                        <CSpinner size="sm" />
                      ) : (
                        <CIcon icon={cilSearch} />
                      )}
                    </CButton>
                    <CFormInput
                      readOnly
                      value={(() => {
                        if (classification && !classification.error)
                          return `${classification.classificacao} — ${classification.concelho}${classification.nuts2 ? ` (${classification.nuts2})` : ""}`;
                        if (classification?.error)
                          return "Código postal não encontrado";
                        return formData.territorial_classification || "";
                      })()}
                      style={(() => {
                        const cls =
                          classification?.classificacao ||
                          formData.territorial_classification;
                        if (classification?.error)
                          return {
                            backgroundColor: "#f8d7da",
                            borderColor: "#dc3545",
                            color: "#842029",
                          };
                        if (cls) {
                          const c = CLASSIFICATION_COLORS[cls] || DEFAULT_COLOR;
                          return {
                            backgroundColor: c.bg,
                            borderColor: c.border,
                            color: c.text,
                          };
                        }
                        return {};
                      })()}
                    />
                  </CInputGroup>
                </CCol>
                <CCol md={6}>
                  <CFormInput
                    label="Condicionantes"
                    name="constraints"
                    value={formData.constraints || ""}
                    onChange={handleInputChange}
                  />
                </CCol>
                <CCol md={12}>
                  <SearchSelect
                    label="Candidatura associada (opcional)"
                    options={applicationOptions}
                    value={formData.application}
                    onChange={handleApplicationChange}
                    placeholder={
                      formData.client
                        ? applicationsLoading
                          ? "A carregar..."
                          : "Selecionar candidatura..."
                        : "Selecione um cliente primeiro"
                    }
                    disabled={!formData.client || applicationsLoading}
                    clearable
                  />
                </CCol>
              </CRow>
            </div>

            <div className="border rounded-3 p-3 bg-body-tertiary mb-4">
              <div className="text-uppercase text-medium-emphasis small mb-3">
                Contactos Empresa
              </div>
              <SheetTableCamp
                fieldId="contacts"
                formData={formData}
                columns={contactColumns}
                setFormData={setFormData}
                maxHeight={400}
              />
            </div>

            <div className="border rounded-3 p-3 bg-body-tertiary mb-4">
              <div className="text-uppercase text-medium-emphasis small mb-3">
                Resumo da atividade da empresa
              </div>
              <TextEditor
                label="Resumo da atividade da empresa"
                value={formData.company_activity_summary || ""}
                onChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    company_activity_summary: value,
                  }))
                }
                className="mb-0"
              />
            </div>

            <div className="border rounded-3 p-3 bg-body-tertiary mb-4">
              <div className="text-uppercase text-medium-emphasis small mb-3">
                Informação do projeto
              </div>
              <CRow className="g-3">
                <CCol md={6}>
                  <CFormSelect
                    label="Tipo de projeto"
                    name="project_type"
                    value={formData.project_type || ""}
                    onChange={handleInputChange}
                  >
                    <option value="">Selecione...</option>
                    {projectTypeOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </CFormSelect>
                </CCol>
                <CCol md={6}>
                  <CFormInput
                    label="Planeamento e calendarização"
                    name="planning_schedule"
                    value={formData.planning_schedule || ""}
                    onChange={handleInputChange}
                  />
                </CCol>
                <CCol md={12}>
                  <TextEditor
                    label="Lista Investimento"
                    value={formData.investment_list || ""}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        investment_list: value,
                      }))
                    }
                    className="mb-3"
                  />
                </CCol>
                <CCol md={12}>
                  <TextEditor
                    label="Inovação/Aspetos diferenciadores"
                    value={formData.innovation_aspects || ""}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        innovation_aspects: value,
                      }))
                    }
                    className="mb-3"
                  />
                </CCol>
                <CCol md={6}>
                  <TextEditor
                    label="Produto"
                    value={formData.product || ""}
                    onChange={(value) =>
                      setFormData((prev) => ({ ...prev, product: value }))
                    }
                    className="mb-3"
                  />
                </CCol>
                <CCol md={6}>
                  <TextEditor
                    label="Processo"
                    value={formData.process || ""}
                    onChange={(value) =>
                      setFormData((prev) => ({ ...prev, process: value }))
                    }
                    className="mb-3"
                  />
                </CCol>
                <CCol md={6}>
                  <TextEditor
                    label="Organizacional"
                    value={formData.organizational || ""}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        organizational: value,
                      }))
                    }
                    className="mb-3"
                  />
                </CCol>
                <CCol md={6}>
                  <TextEditor
                    label="Marketing"
                    value={formData.marketing || ""}
                    onChange={(value) =>
                      setFormData((prev) => ({ ...prev, marketing: value }))
                    }
                    className="mb-3"
                  />
                </CCol>
              </CRow>
            </div>

            <div className="border rounded-3 p-3 bg-body-tertiary mb-4">
              <div className="text-uppercase text-medium-emphasis small mb-3">
                Mercados pós-projeto
              </div>
              <SheetTableCamp
                fieldId="post_project_markets"
                formData={formData}
                columns={marketColumns}
                setFormData={setFormData}
                maxHeight={400}
              />
            </div>
          </CForm>
        </CCardBody>
      </CCard>
    </div>
  );
};

export default KickoffSession;
